import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { isProductInBasket } from "~/utils/isProductInBasket";
import styles from "./index.module.scss";
import { getBacket } from "../../../../redux/action-creaters/basket";
import { showPopup } from "/redux/action-creaters/popup";
import { popupTypes } from "/redux/reducers/popupReducer";
import { ApiClient } from "/api/client";
import Button from "../../../common/button";
import Icon from "../../../common/icon";
import { formatPrice, isMoreThanOneYearAgo } from "../../../../utils";
import {
  CART_GOALS,
  ECOMMERCE_LISTS,
  reachCartGoal,
  useEcommerce,
} from "../../../../utils/tracking";
import InstallPriceNew from "../../../commonNew/product/installPriceNew";
import Split from "../../../common/product/split";

export default function Offer({ product }) {
  const name = product.NAME;
  const productId = product.ID;
  const catalog = product.CATALOG;

  const dispatch = useDispatch();
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const { basket } = useSelector((state) => state.cart);

  const isInCart = useMemo(
    () => isProductInBasket(basket, product),
    [basket, product],
  );

  const quantity = useMemo(() => {
    return catalog.QUANTITY;
  }, [catalog]);

  const isAlailable = useMemo(() => {
    return catalog.AVAILABLE;
  }, [catalog]);

  const price = useMemo(() => {
    return catalog.MIN_PRICE;
  }, [catalog]);

  const noDiscoutPrice = useMemo(() => {
    return catalog.PRICE;
  }, [catalog]);

  const edostMinPriceCorrected = useMemo(() => {
    return 0;
  }, [catalog]);

  const priceWithCity = useMemo(() => {
    return price + edostMinPriceCorrected;
  }, [edostMinPriceCorrected]);
  // eslint-disable-next-line no-unused-vars
  const { user } = useSelector((state) => state.user);

  const { cityName } = useSelector((state) => state.location);
  const { host } = useSelector((state) => state.params);
  const router = useRouter();

  const { trackAdd } = useEcommerce();

  const blockRef = useRef(null);
  const [isShowMobileBuy, setIsShowMobileBuy] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!blockRef.current) return;
      if (window.innerWidth > 991) return;

      const rect = blockRef.current.getBoundingClientRect();
      const offsetTop = rect.top + window.scrollY - 150;
      const { scrollY } = window;

      if (scrollY > offsetTop + blockRef.current.offsetHeight) {
        setIsShowMobileBuy(true);
      } else {
        setIsShowMobileBuy(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // проверка при загрузке

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const buy = async () => {
    if (!isInCart) {
      setIsAddingToCart(true);
      await ApiClient.basket.addProductToBasket(
        product.ID,
        product.CART_PROPS || null,
      );
      await dispatch(getBacket(true));
      await dispatch(showPopup(popupTypes.cart));
      setIsAddingToCart(false);

      reachCartGoal(CART_GOALS.addToCart, user);
      trackAdd(product, { list: ECOMMERCE_LISTS.detail });
    } else {
      router.push("/personal/cart/");
    }
  };

  const subDomain = host.split(".")[0];

  const isSplitAvailableForUser = useMemo(() => {
    // Яндекс Сплит показываем всем пользователям только для товара с id = 133475,
    // а также всегда для менеджеров и администраторов.
    return +productId === 133475 || user.IS_MANAGER || user.IS_ADMIN;
  }, [productId, user]);

  return (
    <>
      <div
        className={styles.Top}
        itemProp="offers"
        itemScope
        itemType="http://schema.org/Offer"
      >
        <div className={styles.Price}>
          {isAlailable && noDiscoutPrice > price && (
            <div className={styles.BeforeValue}>{noDiscoutPrice} руб</div>
          )}
          {!isAlailable && (
            <div className={styles.LastValue}>Последная цена:</div>
          )}
          <div className={styles.Value}>
            <span>
              <span>{formatPrice(priceWithCity)}</span> руб
            </span>
            <span itemProp="price" style={{ display: "none" }}>
              {priceWithCity}
            </span>
            {isAlailable && ["msk", "spb"].includes(subDomain) && (
              <div className={styles.PriceInfo}>
                *стоимость товара без установки
              </div>
            )}
          </div>

          <span itemProp="priceCurrency" style={{ display: "none" }}>
            RUB
          </span>
          {isAlailable ? (
            <>
              <link itemProp="availability" href="http://schema.org/InStock" />
            </>
          ) : (
            <>
              <link
                itemProp="availability"
                href="http://schema.org/OutOfStock"
              />
            </>
          )}
        </div>

        <div className={styles.Qty}>
          {quantity > 0 ? (
            <>
              <Icon id="yes" width={18} height={18} />
              <span className={styles.InStock}>В наличии: {quantity} шт</span>
            </>
          ) : (
            <span className={styles.OutOfStock}>
              {" "}
              {isMoreThanOneYearAgo(product.TIMESTAMP_X)
                ? "Нет в наличии"
                : "Распродано"}
            </span>
          )}
        </div>
      </div>

      {isAlailable &&
        product.MARKA?.CODE_ALT === "lada" &&
        product.IBLOCK_SECTION_ID === 635 &&
        ["Москва", "Санкт-Петербург"].includes(cityName) && (
          <div className={styles.InstallPrice}>
            <InstallPriceNew product={product} />
          </div>
        )}

      <div className={styles.Body} ref={blockRef}>
        {isAlailable ? (
          <>
            <div className={styles.Buttons}>
              <Button
                type={isInCart ? "outline" : ""}
                onClick={buy}
                label={isInCart ? "В корзине" : "Купить"}
                isLoading={isAddingToCart}
              />
              <Button
                type="outline"
                label="Купить в 1 клик"
                onClick={() => {
                  dispatch(
                    showPopup(popupTypes.buyOneClick, {
                      id: productId,
                      price: priceWithCity,
                    }),
                  );
                }}
              />
            </div>
            <span className={styles.Separator} />
            <div
              className={styles.Link}
              data-test="findChiperButton"
              onClick={() => {
                dispatch(
                  showPopup(popupTypes.findLowPrice, {
                    name,
                    id: productId,
                    price: priceWithCity,
                  }),
                );
              }}
            >
              <div aria-label="Нашли дешевле?">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.5 5.5H13C14.3807 5.5 15.5 6.61929 15.5 8C15.5 9.38071 14.3807 10.5 13 10.5H8.5V5.5ZM8.5 5.5V16.5M8.75 10.5H7M12 13.75H7M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z"
                    stroke="#1247A5"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Нашли дешевле?</span>
              </div>

              <svg
                width="8"
                height="14"
                viewBox="0 0 8 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 13L7 7L1 1"
                  stroke="#9DA4AE"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </>
        ) : (
          <div className={styles.ButtonsEmpty}>
            <Button
              type="outline"
              label={"Подобрать аналог"}
              test="findAnalogButton"
              onClick={() => {
                dispatch(
                  showPopup(popupTypes.similarProduct, {
                    name: `${product.NAME} (арт ${product.ARTICLE?.VALUE}) (id ${product.ID})`,
                    id: productId,
                  }),
                );
              }}
            />

            {!isMoreThanOneYearAgo(product.TIMESTAMP_X) && (
              <div
                className={styles.Subscribe}
                data-test="subscribeButton"
                onClick={() => {
                  dispatch(
                    showPopup(popupTypes.subscribeProduct, {
                      name: `${product.NAME} (арт ${product.ARTICLE?.VALUE}) (id ${product.ID})`,
                      id: productId,
                    }),
                  );
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                  <path d="M439.39 362.29c-19.32-20.76-55.47-51.99-55.47-154.29 0-77.7-54.48-139.9-127.94-155.16V32c0-17.67-14.32-32-31.98-32s-31.98 14.33-31.98 32v20.84C118.56 68.1 64.08 130.3 64.08 208c0 102.3-36.15 133.53-55.47 154.29-6 6.45-8.66 14.16-8.61 21.71.11 16.4 12.98 32 32.1 32h383.8c19.12 0 32-15.6 32.1-32 .05-7.55-2.61-15.27-8.61-21.71zM67.53 368c21.22-27.97 44.42-74.33 44.53-159.42 0-.2-.06-.38-.06-.58 0-61.86 50.14-112 112-112s112 50.14 112 112c0 .2-.06.38-.06.58.11 85.1 23.31 131.46 44.53 159.42H67.53zM224 512c35.32 0 63.97-28.65 63.97-64H160.03c0 35.35 28.65 64 63.97 64z" />
                </svg>
              </div>
            )}
          </div>
        )}

        {isSplitAvailableForUser && <Split price={price} />}
      </div>

      {isShowMobileBuy && (
        <div className={styles.BodyMobile}>
          <div className={styles.BodyMobilePrice}>
            <div className={styles.Qty}>
              {quantity > 0 ? (
                <span className={styles.InStock}>В наличии: {quantity} шт</span>
              ) : (
                <span className={styles.OutOfStock}>
                  {isMoreThanOneYearAgo(product.TIMESTAMP_X)
                    ? "Нет в наличии"
                    : "Распродано"}
                </span>
              )}
            </div>
            <div className={styles.Price}>
              <div className={styles.Value}>
                {isAlailable && (
                  <>
                    <span>
                      <span>{formatPrice(priceWithCity)}</span> руб
                    </span>
                    <span itemProp="price" style={{ display: "none" }}>
                      {priceWithCity}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={styles.BodyMobileButtons}>
            {!isAlailable ? (
              <div className={styles.ButtonsEmpty}>
                <Button
                  type="outline"
                  label={"Подобрать аналог"}
                  onClick={() => {
                    dispatch(
                      showPopup(popupTypes.similarProduct, {
                        name: `${product.NAME} (арт ${product.ARTICLE?.VALUE}) (id ${product.ID})`,
                        id: productId,
                      }),
                    );
                  }}
                />

                {!isMoreThanOneYearAgo(product.TIMESTAMP_X) && (
                  <div
                    className={styles.Subscribe}
                    onClick={() => {
                      dispatch(
                        showPopup(popupTypes.subscribeProduct, {
                          name: `${product.NAME} (арт ${product.ARTICLE?.VALUE}) (id ${product.ID})`,
                          id: productId,
                        }),
                      );
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                    >
                      <path d="M439.39 362.29c-19.32-20.76-55.47-51.99-55.47-154.29 0-77.7-54.48-139.9-127.94-155.16V32c0-17.67-14.32-32-31.98-32s-31.98 14.33-31.98 32v20.84C118.56 68.1 64.08 130.3 64.08 208c0 102.3-36.15 133.53-55.47 154.29-6 6.45-8.66 14.16-8.61 21.71.11 16.4 12.98 32 32.1 32h383.8c19.12 0 32-15.6 32.1-32 .05-7.55-2.61-15.27-8.61-21.71zM67.53 368c21.22-27.97 44.42-74.33 44.53-159.42 0-.2-.06-.38-.06-.58 0-61.86 50.14-112 112-112s112 50.14 112 112c0 .2-.06.38-.06.58.11 85.1 23.31 131.46 44.53 159.42H67.53zM224 512c35.32 0 63.97-28.65 63.97-64H160.03c0 35.35 28.65 64 63.97 64z" />
                    </svg>
                  </div>
                )}
              </div>
            ) : (
              <Button
                type={isInCart ? "outline" : ""}
                onClick={buy}
                label={isInCart ? "В корзине" : "Купить"}
                isLoading={isAddingToCart}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
